import { useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import { Table } from "reactstrap";
import { Button } from "../../Styled";
import { ModalBox, ModalOverlay } from "../../Modals/ModalStyled";
import {
  getLineReviewKey,
  OBSOLETE_ALT_SELECTED_ROW_CLASS,
} from "../../../constants/digiKeyColumns";

const formatPrice = (option) => {
  if (option.unit_price_display) {
    return option.unit_price_display;
  }
  if (option.unit_price != null) {
    return `$${Number(option.unit_price).toFixed(5)}`;
  }
  return "N/A";
};

const formatAvailability = (option) => {
  const value = option.quantity_available;
  if (value == null || String(value).trim() === "") {
    return "0";
  }
  return String(value).replace(/,/g, "");
};

const hasStock = (option) => {
  const qty = Number(String(option?.quantity_available ?? "0").replace(/,/g, ""));
  return qty > 0;
};

const sortByUnitPrice = (items) =>
  [...items].sort((a, b) => {
    const priceA = a.unit_price != null ? Number(a.unit_price) : Infinity;
    const priceB = b.unit_price != null ? Number(b.unit_price) : Infinity;
    return priceA - priceB;
  });

const cheapestMpn = (items) => {
  const sorted = sortByUnitPrice(items);
  return sorted.length > 0
    ? String(sorted[0].manufacturer_part_number || "").trim()
    : "";
};

const ViewOptionsTable = ({ options, selectedMpn, onSelect, radioName }) => {
  if (!options.length) {
    return (
      <p className="text-muted mb-0">
        No suggested parts were returned by DigiKey for this MPN.
      </p>
    );
  }

  return (
    <Table className="table-bordered table-sm mb-0 unmatched-view-options-table parts-review-table">
      <thead>
          <tr>
            <th style={{ width: "36px" }} />
            <th>Part Number</th>
            <th style={{ width: "18%" }}>Availability</th>
            <th style={{ width: "18%" }}>Unit Price</th>
          </tr>
      </thead>
      <tbody>
        {options.map((option, index) => {
          const mpn = String(option.manufacturer_part_number || "").trim();
          const isSelected = selectedMpn === mpn;

          return (
            <tr
              key={`${mpn}-${index}`}
              className={classnames({
                [OBSOLETE_ALT_SELECTED_ROW_CLASS]: isSelected,
              })}
              onClick={() => onSelect(mpn)}
              style={{ cursor: "pointer" }}
            >
              <td>
                <input
                  type="radio"
                  name={radioName}
                  checked={isSelected}
                  onChange={() => onSelect(mpn)}
                />
              </td>
              <td>
                <div className="unmatched-option-mpn">{mpn}</div>
                {option.description ? (
                  <div className="text-muted small">{option.description}</div>
                ) : null}
                {option.manufacturer ? (
                  <div className="text-muted small">{option.manufacturer}</div>
                ) : null}
                {option.package ? (
                  <div className="text-muted small">{option.package}</div>
                ) : null}
              </td>
              <td>{formatAvailability(option)}</td>
              <td>
                <div>{formatPrice(option)}</div>
                {option.package ? (
                  <div className="text-muted small">{option.package}</div>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const UnmatchedPartsReviewModal = ({
  show,
  reviewLine,
  token,
  onSave,
  onClose,
}) => {
  const line = reviewLine || null;
  const lineKey = line ? getLineReviewKey(line) : "";
  const originalMpn = String(
    line?.unmatched_original_mpn || line?.manufacturer_part_number || ""
  ).trim();

  const [options, setOptions] = useState([]);
  const [totalFound, setTotalFound] = useState(0);
  const [selectedMpn, setSelectedMpn] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!show || !line) {
      return;
    }

    setSelectedMpn(
      line.selected_alternate_mpn
        ? String(line.selected_alternate_mpn).trim()
        : ""
    );
    setLoadError("");
  }, [show, line, lineKey]);

  useEffect(() => {
    if (!show || !line) {
      return;
    }

    const cachedOptions = Array.isArray(line.substitutes)
      ? line.substitutes.filter(
          (item) =>
            String(item?.manufacturer_part_number || "").trim() !== "" &&
            hasStock(item)
        )
      : [];
    const hasCache =
      line.substitutes_loaded === true ||
      cachedOptions.length > 1 ||
      (cachedOptions.length === 1 && !line.selected_alternate_mpn);

    if (!hasCache) {
      setLoading(true);
      setOptions([]);
      setTotalFound(0);
      return;
    }

    const sorted = sortByUnitPrice(cachedOptions);
    setOptions(sorted);
    setTotalFound(sorted.length);
    setLoading(false);
    setLoadError("");
    if (!line.selected_alternate_mpn) {
      setSelectedMpn((current) => current || cheapestMpn(sorted));
    }
  }, [show, line, lineKey, line?.substitutes, line?.substitutes_loaded]);

  const selectedOption = useMemo(
    () =>
      options.find(
        (item) =>
          String(item.manufacturer_part_number || "").trim() === selectedMpn
      ) || null,
    [options, selectedMpn]
  );

  if (!show || !line) {
    return null;
  }

  const handleSave = () => {
    if (!selectedOption) {
      return;
    }
    onSave({ line, alternate: selectedOption, options });
  };

  return (
    <ModalOverlay className={classnames({ show })}>
      <ModalBox
        className="theme-form unmatched-view-options-modal"
        style={{
          maxWidth: "1600px",
          width: "calc(100vw - 48px)",
          maxHeight: "90vh",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <div className="unmatched-parts-modal-header">
          <h5 className="mb-0">Exact Match Not Found</h5>
          <button
            type="button"
            className="unmatched-parts-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div className="unmatched-view-options-meta mb-3">
          <div>
            <strong>{originalMpn}</strong>
          </div>
          <div className="text-muted small">
            Results: {loading ? "…" : totalFound}
          </div>
        </div>

        <p className="text-muted mb-3">
          Please modify your entry or select a suggested part below.
        </p>

        {loadError ? <p className="text-danger mb-2">{loadError}</p> : null}
        {loading ? (
          <p className="text-muted mb-3">
            Loading suggested parts in the background. You can close this and
            open another unmatched part.
          </p>
        ) : (
          <ViewOptionsTable
            options={options}
            selectedMpn={selectedMpn}
            radioName={`unmatched-view-option-${lineKey}`}
            onSelect={setSelectedMpn}
          />
        )}

        <div className="unmatched-parts-modal-footer d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted small">
            {selectedMpn ? `Selected: ${selectedMpn}` : "Select a suggested part"}
          </span>
          <div className="unmatched-parts-modal-actions d-flex align-items-center">
            <Button
              type="button"
              className="importBtn blue px-4 w-auto mr-2"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="importBtn blue px-4 w-auto"
              disabled={loading || !selectedOption}
              onClick={handleSave}
            >
              Save Selection
            </Button>
          </div>
        </div>
      </ModalBox>
    </ModalOverlay>
  );
};

export default UnmatchedPartsReviewModal;
